import { supabase } from './supabase';
import type { Task, Column, RecurrencePattern } from '@/components/kanban/types';

const boardColumnsCache = new Map<string, Column[]>();
const boardTasksCache = new Map<string, Task[]>();
const prefetchPromises = new Map<string, Promise<any>>();

export const taskService = {
  clearCache() {
    boardColumnsCache.clear();
    boardTasksCache.clear();
    prefetchPromises.clear();
  },

  // ============= COLUMNS =============
  async getColumnsByBoard(boardId: string) {
    if (boardColumnsCache.has(boardId)) {
      return boardColumnsCache.get(boardId)!;
    }

    const { data, error } = await supabase
      .from('task_columns')
      .select('*')
      .eq('board_id', boardId)
      .order('position', { ascending: true });

    if (error) throw error;

    boardColumnsCache.set(boardId, data || []);
    return data || [];
  },

  async createColumn(boardId: string, title: string, position: number, bgColor?: string) {
    const { data, error } = await supabase
      .from('task_columns')
      .insert({
        board_id: boardId,
        title,
        position,
        bg_color: bgColor,
      })
      .select()
      .single();

    if (error) throw error;

    // Invalidate cache
    boardColumnsCache.delete(boardId);

    return data;
  },

  async updateColumn(columnId: string, updates: { title?: string; position?: number; bg_color?: string }) {
    const { data, error } = await supabase
      .from('task_columns')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', columnId)
      .select()
      .single();

    if (error) throw error;

    // Invalidate cache
    if (data) boardColumnsCache.delete(data.board_id);

    return data;
  },

  async deleteColumn(columnId: string) {
    // We need board_id to invalidate cache, so we fetch it first or just clear all
    const { data: col } = await supabase.from('task_columns').select('board_id').eq('id', columnId).maybeSingle();

    const { error } = await supabase
      .from('task_columns')
      .delete()
      .eq('id', columnId);

    if (error) throw error;

    if (col) boardColumnsCache.delete(col.board_id);
  },

  async reorderColumns(boardId: string, columnOrder: string[]) {
    // Update the position of each column based on its index in the array
    const updates = columnOrder.map((columnId, index) =>
      supabase
        .from('task_columns')
        .update({ position: index, updated_at: new Date().toISOString() })
        .eq('id', columnId)
        .eq('board_id', boardId)
    );

    const results = await Promise.all(updates);

    // Invalidate cache
    boardColumnsCache.delete(boardId);

    // Check for errors
    const errors = results.filter(r => r.error);
    if (errors.length > 0) {
      console.error('Reorder columns errors:', errors);
      throw errors[0].error;
    }
  },

  // ============= TASKS =============
  async getTasksByBoard(boardId: string) {
    if (boardTasksCache.has(boardId)) {
      return boardTasksCache.get(boardId)!;
    }

    // Get tasks
    const { data: tasks, error: tasksError } = await supabase
      .from('tasks')
      .select('*')
      .eq('board_id', boardId)
      .order('position', { ascending: true });

    if (tasksError) throw tasksError;
    if (!tasks || tasks.length === 0) {
      boardTasksCache.set(boardId, []);
      return [];
    }

    const taskIds = tasks.map(t => t.id);

    // Get assignees for all tasks
    const { data: assignees } = await supabase
      .from('task_assignees')
      .select('task_id, user_id')
      .in('task_id', taskIds);

    // Get profiles for assignees and creators
    const userIds = assignees?.map(a => a.user_id) || [];
    const creatorIds = tasks.map(t => t.created_by).filter(Boolean);
    const allUserIds = Array.from(new Set([...userIds, ...creatorIds]));
    
    const { data: profiles } = allUserIds.length > 0
      ? await supabase
        .from('profiles')
        .select('id, email, first_name, last_name, avatar_url')
        .in('id', allUserIds)
      : { data: [] };

    // Get time tracking
    const { data: timeTracking } = await supabase
      .from('task_time_tracking')
      .select('*')
      .in('task_id', taskIds);

    // Get messages
    const { data: messages } = await supabase
      .from('task_messages')
      .select('*')
      .in('task_id', taskIds)
      .order('created_at', { ascending: true });

    // Get attachments
    const messageIds = messages?.map(m => m.id) || [];
    const { data: attachments } = messageIds.length > 0
      ? await supabase
        .from('task_attachments')
        .select('*')
        .in('message_id', messageIds)
      : { data: [] };

    // Combine all data
    const combinedTasks = tasks.map(task => {
      const creatorProfile = profiles?.find(p => p.id === task.created_by);
      return {
        ...task,
        creator: creatorProfile ? {
          id: creatorProfile.id,
          name: `${creatorProfile.first_name || ''} ${creatorProfile.last_name || ''}`.trim() || creatorProfile.email,
          avatar: creatorProfile.avatar_url,
        } : undefined,
        task_assignees: assignees?.filter(a => a.task_id === task.id).map(a => ({
          user_id: a.user_id,
          profiles: profiles?.find(p => p.id === a.user_id),
        })) || [],
        task_time_tracking: timeTracking?.filter(tt => tt.task_id === task.id) || [],
        task_messages: messages?.filter(m => m.task_id === task.id).map(m => ({
          ...m,
          task_attachments: attachments?.filter(att => att.message_id === m.id) || [],
        })) || [],
      };
    });

    boardTasksCache.set(boardId, combinedTasks as any);
    return combinedTasks;
  },

  async prefetchBoardContent(boardId: string) {
    if ((boardColumnsCache.has(boardId) && boardTasksCache.has(boardId)) || prefetchPromises.has(boardId)) {
      return;
    }

    const promise = Promise.all([
      this.getColumnsByBoard(boardId),
      this.getTasksByBoard(boardId)
    ]).finally(() => {
      prefetchPromises.delete(boardId);
    });

    prefetchPromises.set(boardId, promise);
    return promise;
  },

  async createTask(
    boardId: string,
    data: {
      title: string;
      description?: string;
      priority: string;
      columnId?: string;
      position: number;
      startDate?: string;
      dueDate?: string;
      createdBy: string;
      isRecurring?: boolean;
      recurrencePattern?: RecurrencePattern;
    }
  ) {
    const { data: task, error } = await supabase
      .from('tasks')
      .insert({
        board_id: boardId,
        workspace_id: '', // Will be set by trigger or we need to pass it
        column_id: data.columnId,
        title: data.title,
        description: data.description || '',
        priority: data.priority,
        position: data.position,
        start_date: data.startDate,
        due_date: data.dueDate,
        created_by: data.createdBy,
        is_recurring: data.isRecurring || false,
        recurrence_pattern: data.recurrencePattern || null,
      })
      .select()
      .single();

    if (error) throw error;

    // Invalidate cache
    boardTasksCache.delete(boardId);

    return task;
  },

  async updateTask(
    taskId: string,
    updates: {
      title?: string;
      description?: string | null;
      priority?: string;
      column_id?: string | null;
      position?: number;
      status?: string;
      start_date?: string | null;
      due_date?: string | null;
      archived_at?: string | null;
      completed?: boolean;
      completed_at?: string | null;
      is_recurring?: boolean;
      recurrence_pattern?: any | null;
      cover_image_url?: string | null;
      upvotes?: number;
      upvoted_by?: string[];
    }
  ) {
    let dbUpdates: any = { ...updates, updated_at: new Date().toISOString() };
    let { data, error } = await supabase
      .from('tasks')
      .update(dbUpdates)
      .eq('id', taskId)
      .select()
      .single();

    // If upvotes or upvoted_by columns don't exist yet on remote table, gracefully retry without them
    if (error && (error.message?.includes('upvotes') || error.message?.includes('upvoted_by') || error.code === '42703')) {
      delete dbUpdates.upvotes;
      delete dbUpdates.upvoted_by;
      const retry = await supabase
        .from('tasks')
        .update(dbUpdates)
        .eq('id', taskId)
        .select()
        .single();
      data = retry.data;
      error = retry.error;
    }

    if (error) throw error;

    // Invalidate cache
    if (data) boardTasksCache.delete(data.board_id);

    return data;
  },

  async moveTask(taskId: string, toColumnId: string, toPosition: number) {
    // Get the task's current column
    const { data: task, error: taskError } = await supabase
      .from('tasks')
      .select('column_id, position, board_id')
      .eq('id', taskId)
      .maybeSingle();

    if (taskError) throw taskError;
    if (!task) throw new Error('Task not found');

    const fromColumnId = task.column_id;
    const boardId = task.board_id;

    // If moving to same column at same position, do nothing
    if (fromColumnId === toColumnId && task.position === toPosition) {
      return;
    }

    // Get all tasks in the target column
    let targetColumnQuery = supabase
      .from('tasks')
      .select('id, column_id, position')
      .eq('board_id', boardId)
      .eq('column_id', toColumnId)
      .eq('status', 'active')
      .order('position', { ascending: true });

    const { data: targetTasks, error: targetError } = await targetColumnQuery;
    if (targetError) throw targetError;

    // Get all tasks in the source column if different from target
    let sourceTasks: any[] = [];
    if (fromColumnId && fromColumnId !== toColumnId) {
      const { data: srcTasks, error: sourceError } = await supabase
        .from('tasks')
        .select('id, column_id, position')
        .eq('board_id', boardId)
        .eq('column_id', fromColumnId)
        .eq('status', 'active')
        .order('position', { ascending: true });

      if (sourceError) throw sourceError;
      sourceTasks = srcTasks || [];
    }

    // Remove the task being moved from both lists
    const fromTasks = sourceTasks.filter(t => t.id !== taskId);
    const toTasks = (targetTasks || []).filter(t => t.id !== taskId);

    // Insert the moved task at the new position
    toTasks.splice(toPosition, 0, { id: taskId, column_id: toColumnId, position: toPosition });

    // Prepare all updates
    const updates = [];

    // Update task being moved
    updates.push(
      supabase
        .from('tasks')
        .update({
          column_id: toColumnId,
          position: toPosition,
          status: 'active',
          updated_at: new Date().toISOString()
        })
        .eq('id', taskId)
    );

    // Update positions in source column (if different from target)
    if (fromColumnId !== toColumnId && fromColumnId) {
      fromTasks.forEach((t, idx) => {
        updates.push(
          supabase
            .from('tasks')
            .update({ position: idx, updated_at: new Date().toISOString() })
            .eq('id', t.id)
        );
      });
    }

    // Update positions in target column for other tasks
    toTasks.forEach((t, idx) => {
      if (t.id !== taskId) {
        updates.push(
          supabase
            .from('tasks')
            .update({ position: idx, updated_at: new Date().toISOString() })
            .eq('id', t.id)
        );
      }
    });

    // Execute all updates in parallel
    const results = await Promise.all(updates);

    // Invalidate cache
    boardTasksCache.delete(boardId);

    // Check for errors
    const errors = results.filter(r => r.error);
    if (errors.length > 0) {
      console.error('Move task errors:', errors);
      throw errors[0].error;
    }
  },

  async deleteTask(taskId: string) {
    const { data: task } = await supabase.from('tasks').select('board_id').eq('id', taskId).maybeSingle();

    const { error } = await supabase
      .from('tasks')
      .delete()
      .eq('id', taskId);

    if (error) throw error;

    // Invalidate cache
    if (task) boardTasksCache.delete(task.board_id);
  },

  async bulkUpdateTaskPositions(updates: { id: string; column_id?: string; position: number }[]) {
    // This is more complex for cache invalidation, let's just clear all tasks for affected boards
    // For simplicity, we can clear all or just fetch board ids if possible
    const promises = updates.map(({ id, column_id, position }) =>
      supabase
        .from('tasks')
        .update({ column_id, position, updated_at: new Date().toISOString() })
        .eq('id', id)
    );

    await Promise.all(promises);

    // Invalidate all tasks cache to be safe as we don't know the board_ids easily here without extra query
    boardTasksCache.clear();
  },

  // ============= ASSIGNEES =============
  async addAssignee(taskId: string, userId: string) {
    const { data, error } = await supabase
      .from('task_assignees')
      .insert({
        task_id: taskId,
        user_id: userId,
      })
      .select()
      .single();

    if (error) {
      if (error.code === '23505') return null; // Already assigned
      throw error;
    }

    // Invalidate cache for the board containing this task
    const { data: task } = await supabase.from('tasks').select('board_id').eq('id', taskId).maybeSingle();
    if (task) boardTasksCache.delete(task.board_id);

    return data;
  },

  async removeAssignee(taskId: string, userId: string) {
    const { error } = await supabase
      .from('task_assignees')
      .delete()
      .eq('task_id', taskId)
      .eq('user_id', userId);

    if (error) throw error;

    // Invalidate cache for the board containing this task
    const { data: task } = await supabase.from('tasks').select('board_id').eq('id', taskId).maybeSingle();
    if (task) boardTasksCache.delete(task.board_id);
  },

  async getAssignees(taskId: string) {
    const { data, error } = await supabase
      .from('task_assignees')
      .select('user_id, profiles(id, email, first_name, last_name, avatar_url)')
      .eq('task_id', taskId);

    if (error) throw error;
    return data || [];
  },

  // ============= TIME TRACKING =============
  async getTimeTracking(taskId: string) {
    const { data, error } = await supabase
      .from('task_time_tracking')
      .select('*')
      .eq('task_id', taskId)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async upsertTimeTracking(taskId: string, timeData: {
    total_seconds: number;
    is_running: boolean;
    last_start_time?: string;
  }) {
    const { data, error } = await supabase
      .from('task_time_tracking')
      .upsert({
        task_id: taskId,
        ...timeData,
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;

    // Invalidate cache for task board if we include time tracking in tasks
    const { data: task } = await supabase.from('tasks').select('board_id').eq('id', taskId).maybeSingle();
    if (task) boardTasksCache.delete(task.board_id);

    return data;
  },

  // ============= MESSAGES =============
  async addMessage(taskId: string, userId: string, text: string, attachments?: Array<{name: string, size: number, type: string, url: string, isExternalGif?: boolean}>) {
    const { data, error } = await supabase
      .from('task_messages')
      .insert({
        task_id: taskId,
        user_id: userId,
        text,
      })
      .select()
      .single();

    if (error) throw error;

    // If there are attachments, save them to task_attachments
    if (attachments && attachments.length > 0 && data) {
      const attachmentRecords = attachments.map(att => ({
        task_id: taskId,
        message_id: data.id,
        name: att.name,
        size: att.size,
        type: att.type,
        url: att.url,
        uploaded_by: userId,
        is_external_gif: att.isExternalGif || false,
      }));

      const { error: attError } = await supabase
        .from('task_attachments')
        .insert(attachmentRecords);

      if (attError) {
        console.error('Error saving attachments:', attError);
        // Don't throw - message was saved successfully
      }
    }

    // Invalidate cache for task board
    const { data: task } = await supabase.from('tasks').select('board_id').eq('id', taskId).maybeSingle();
    if (task) boardTasksCache.delete(task.board_id);

    return data;
  },

  async getMessages(taskId: string) {
    const { data, error } = await supabase
      .from('task_messages')
      .select('*, task_attachments(*)')
      .eq('task_id', taskId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async deleteMessage(messageId: string) {
    // Get task_id to invalidate cache
    const { data: msg } = await supabase.from('task_messages').select('task_id').eq('id', messageId).maybeSingle();

    const { error } = await supabase
      .from('task_messages')
      .delete()
      .eq('id', messageId);

    if (error) throw error;

    if (msg) {
      const { data: task } = await supabase.from('tasks').select('board_id').eq('id', msg.task_id).maybeSingle();
      if (task) boardTasksCache.delete(task.board_id);
    }
  },

  // ============= ATTACHMENTS =============
  async addAttachment(data: {
    taskId?: string;
    messageId?: string;
    name: string;
    size: number;
    type: string;
    url: string;
    uploadedBy: string;
  }) {
    const { data: attachment, error } = await supabase
      .from('task_attachments')
      .insert({
        task_id: data.taskId,
        message_id: data.messageId,
        name: data.name,
        size: data.size,
        type: data.type,
        url: data.url,
        uploaded_by: data.uploadedBy,
      })
      .select()
      .single();

    if (error) throw error;

    // Invalidate cache
    if (data.taskId) {
      const { data: task } = await supabase.from('tasks').select('board_id').eq('id', data.taskId).maybeSingle();
      if (task) boardTasksCache.delete(task.board_id);
    } else if (data.messageId) {
      const { data: msg } = await supabase.from('task_messages').select('task_id').eq('id', data.messageId).maybeSingle();
      if (msg) {
        const { data: task } = await supabase.from('tasks').select('board_id').eq('id', msg.task_id).maybeSingle();
        if (task) boardTasksCache.delete(task.board_id);
      }
    }

    return attachment;
  },

  async deleteAttachment(attachmentId: string) {
    // Get task_id or message_id to invalidate cache
    const { data: att } = await supabase.from('task_attachments').select('task_id, message_id').eq('id', attachmentId).maybeSingle();

    const { error } = await supabase
      .from('task_attachments')
      .delete()
      .eq('id', attachmentId);

    if (error) throw error;

    if (att) {
      if (att.task_id) {
        const { data: task } = await supabase.from('tasks').select('board_id').eq('id', att.task_id).maybeSingle();
        if (task) boardTasksCache.delete(task.board_id);
      } else if (att.message_id) {
        const { data: msg } = await supabase.from('task_messages').select('task_id').eq('id', att.message_id).maybeSingle();
        if (msg) {
          const { data: task } = await supabase.from('tasks').select('board_id').eq('id', msg.task_id).maybeSingle();
          if (task) boardTasksCache.delete(task.board_id);
        }
      }
    }
  },
};
